/**
 * Draws the shareable "year in review" card with the Canvas 2D API.
 * Plain canvas (no html-to-image) so book covers from other origins can't taint it
 * and the output looks the same on every browser. 1080x1350 fits Instagram/WhatsApp.
 */

export interface ShareImageStat {
  label: string
  value: string
  detail?: string
}

export interface ShareImageContent {
  year: number
  title: string
  booksRead: number
  booksReadLabel: string
  highlights: ShareImageStat[]
  footerStats: string[]
  brand: string
  url: string
}

const WIDTH = 1080
const HEIGHT = 1350
const PADDING = 80
const FONT = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'

export async function renderShareImage(content: ShareImageContent): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')

  await document.fonts?.ready

  drawBackground(ctx)

  // Brand header
  const logo = await loadImage('/logo.png').catch(() => null)
  const logoSize = 88
  if (logo) {
    roundedRect(ctx, PADDING, PADDING, logoSize, logoSize, 22)
    ctx.fillStyle = 'rgba(255,255,255,0.95)'
    ctx.fill()
    ctx.drawImage(logo, PADDING + 8, PADDING + 8, logoSize - 16, logoSize - 16)
  }
  ctx.fillStyle = '#ffffff'
  ctx.font = `700 44px ${FONT}`
  ctx.textBaseline = 'middle'
  ctx.fillText(content.brand, PADDING + (logo ? logoSize + 24 : 0), PADDING + logoSize / 2)

  // Title + year
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = 'rgba(255,255,255,0.75)'
  ctx.font = `500 40px ${FONT}`
  ctx.fillText(content.title, PADDING, 300)

  ctx.fillStyle = '#ffffff'
  ctx.font = `800 150px ${FONT}`
  ctx.fillText(String(content.year), PADDING - 6, 445)

  // Hero number
  const heroTop = 500
  roundedRect(ctx, PADDING, heroTop, WIDTH - PADDING * 2, 210, 36)
  ctx.fillStyle = 'rgba(255,255,255,0.12)'
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.font = `800 130px ${FONT}`
  const heroText = String(content.booksRead)
  ctx.fillText(heroText, PADDING + 48, heroTop + 152)
  const heroWidth = ctx.measureText(heroText).width
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  ctx.font = `600 46px ${FONT}`
  fitText(ctx, content.booksReadLabel, PADDING + 48 + heroWidth + 32, heroTop + 140, 520)

  // Highlights: 2 columns
  const gridTop = 750
  const gap = 28
  const tileWidth = (WIDTH - PADDING * 2 - gap) / 2
  const tileHeight = 170
  content.highlights.slice(0, 4).forEach((stat, i) => {
    const x = PADDING + (i % 2) * (tileWidth + gap)
    const y = gridTop + Math.floor(i / 2) * (tileHeight + gap)
    roundedRect(ctx, x, y, tileWidth, tileHeight, 28)
    ctx.fillStyle = 'rgba(255,255,255,0.1)'
    ctx.fill()

    ctx.fillStyle = 'rgba(255,255,255,0.7)'
    ctx.font = `500 28px ${FONT}`
    fitText(ctx, stat.label, x + 32, y + 52, tileWidth - 64)

    ctx.fillStyle = '#ffffff'
    ctx.font = `700 40px ${FONT}`
    fitText(ctx, stat.value, x + 32, y + 104, tileWidth - 64)

    if (stat.detail) {
      ctx.fillStyle = 'rgba(255,255,255,0.65)'
      ctx.font = `500 26px ${FONT}`
      fitText(ctx, stat.detail, x + 32, y + 144, tileWidth - 64)
    }
  })

  // Footer
  ctx.fillStyle = 'rgba(255,255,255,0.8)'
  ctx.font = `500 30px ${FONT}`
  fitText(ctx, content.footerStats.join('  ·  '), PADDING, HEIGHT - 150, WIDTH - PADDING * 2)

  ctx.fillStyle = 'rgba(255,255,255,0.55)'
  ctx.font = `600 30px ${FONT}`
  ctx.fillText(content.url, PADDING, HEIGHT - PADDING)

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Export failed'))),
      'image/png'
    )
  )
}

function drawBackground(ctx: CanvasRenderingContext2D) {
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT)
  gradient.addColorStop(0, '#1e3a8a')
  gradient.addColorStop(0.55, '#4c1d95')
  gradient.addColorStop(1, '#831843')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, WIDTH, HEIGHT)

  // Soft light blobs for depth
  const blob = (x: number, y: number, r: number, color: string) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r)
    g.addColorStop(0, color)
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, WIDTH, HEIGHT)
  }
  blob(WIDTH * 0.9, HEIGHT * 0.1, 520, 'rgba(96,165,250,0.35)')
  blob(WIDTH * 0.05, HEIGHT * 0.95, 600, 'rgba(236,72,153,0.25)')
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** Draws text truncated with an ellipsis so it never overflows its box */
function fitText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, max: number) {
  if (ctx.measureText(text).width <= max) {
    ctx.fillText(text, x, y)
    return
  }
  let end = text.length
  while (end > 0 && ctx.measureText(`${text.slice(0, end)}…`).width > max) end--
  ctx.fillText(`${text.slice(0, end).trimEnd()}…`, x, y)
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}
